import { useState, useEffect } from "react";
import { PRICE_PLACEHOLDER, getDecimalSeparator, splitAmount, formatPrice } from "@/func/priceNotation";

/**
 * Splits an amount into the pieces the product cards render separately - the whole part, the cents
 * and the currency symbol.
 *
 * Both the cents separator and the symbol come from the currency string rather than from splitting
 * the rendered price on a guessed separator, so an EU price parses the same way as a US one.
 * `decimalSeparator` is returned because the cards join the two number parts back together.
 */
const currencyParser = (amount, currencyCode, formattedString) => {
  const decimalSeparator = getDecimalSeparator(formattedString);
  const { whole, cents } = splitAmount(amount, decimalSeparator);

  const placeholder = formattedString?.match(PRICE_PLACEHOLDER);
  const beforeAmount = placeholder ? formattedString.slice(0, placeholder.index) : "";
  const afterAmount = placeholder ? formattedString.slice(placeholder.index + placeholder[0].length) : "";
  const symbolLeft = beforeAmount.trim().length > 0;

  return {
    symbolLeft,
    number: whole,
    cents,
    decimalSeparator,
    symbol: symbolLeft ? beforeAmount.replace(/\s+$/, "\u00A0") : afterAmount.replace(/^\s+/, "\u00A0"),
    hasCents: currencyCode !== "JPY",
  };
};

const useReducedProductsPrice = (product, currencyCode, formattedString) => {
  const [price, setPrice] = useState("0.00");
  const [promoPrice, setPromoPrice] = useState("0.00");
  const [oldPrice, setOldPrice] = useState("0.00");

  const [priceObject, setPriceObject] = useState(null);
  const [promoPriceObject, setPromoPriceObject] = useState(null);

  useEffect(() => {
    if (product) {
      setPrice(Number(product?.price).toFixed(2));
      setOldPrice(formatPrice(product.old_price, formattedString));
      setPromoPrice(Number(product?.promo_discounted_price)?.toFixed(2));
    }
  }, [product]);

  useEffect(() => {
    if (price && formattedString) {
      const parsedPriceObject = currencyParser(price, currencyCode, formattedString);
      setPriceObject(parsedPriceObject);
    }
  }, [price, formattedString, currencyCode]);

  useEffect(() => {
    if (promoPrice && formattedString) {
      const parsedPromoPriceObject = currencyParser(promoPrice, currencyCode, formattedString);
      setPromoPriceObject(parsedPromoPriceObject);
    }
  }, [promoPrice, currencyCode]);

  return { price, priceObject, promoPriceObject, oldPrice };
};

export { currencyParser, useReducedProductsPrice };
